// Tile grid / admin tile overlay draw helpers extracted 1:1 from RoomCanvas.tsx.
// Pure PIXI drawing — refs read on the caller side become plain params here.
import * as PIXI from 'pixi.js';
import {
  TILE_WIDTH,
  TILE_HEIGHT,
  MAP_WIDTH_TILES,
  FLOOR_WIDTH,
  FLOOR_HEIGHT,
} from '../../../domain/grid';
import { mirrorFpOffset } from '../../../domain/tileMath';
import { getOccupiedBaseTile, type DepthComponents } from '../../../domain/depthMath';

// Green selection outline around a marker's local bounds. Extracted 1:1 from
// RoomCanvas.tsx showGreenOutline(); ref bookkeeping + addChild stay in the component.
export function makeGreenOutlineGraphic(
  bounds: { x: number; y: number; width: number; height: number },
  strong = false,
): PIXI.Graphics {
  const gfx = new PIXI.Graphics();
  if (strong) {
    // A just-bought item: a thick outline with a soft glow, so it is found at once.
    gfx.roundRect(bounds.x - 7, bounds.y - 7, bounds.width + 14, bounds.height + 14, 10);
    gfx.stroke({ width: 10, color: 0x00FF00, alpha: 0.25 });
    gfx.roundRect(bounds.x - 7, bounds.y - 7, bounds.width + 14, bounds.height + 14, 10);
    gfx.stroke({ width: 4, color: 0x00FF00, alpha: 1 });
    return gfx;
  }
  gfx.rect(bounds.x - 4, bounds.y - 4, bounds.width + 8, bounds.height + 8);
  gfx.stroke({ width: 2, color: 0x00FF00, alpha: 0.8 });
  return gfx;
}

// Build-mode grid palette. The old acid green (0x00ff90, width 2, effective
// alpha 0.855) fought the furniture for attention and read as an error state.
// A cool light blue sits on top of the warm wood/plant palette of the interiors
// without competing with it, and the grid is a guide — it must stay legible
// while the room, not the grid, remains the thing you look at.
const GRID_LINE_COLOR = 0x9fd8ff;
const GRID_LINE_ALPHA = 0.34;
// 1 world px: the grid is drawn inside the room container, so this thins out
// further as the camera zooms out — which is exactly when it must get out of the way.
const GRID_LINE_WIDTH = 1;
// The floor outline stays a touch stronger than the inner lines: it is the one
// line that carries meaning (where the walkable floor ends).
const GRID_BORDER_COLOR = 0x7ec8f2;
const GRID_BORDER_ALPHA = 0.55;
const GRID_BORDER_WIDTH = 2;

export function drawTileGridOverlay(target: PIXI.Graphics): void {
  target.clear();
  // Opacity lives entirely in the strokes below, so there is a single source of
  // truth for how loud the grid is (previously alpha was multiplied twice).
  target.alpha = 1;
  target.eventMode = 'none';

  const halfTileWidth = TILE_WIDTH / 2;
  const halfTileHeight = TILE_HEIGHT / 2;
  const maxTileIndex = MAP_WIDTH_TILES;

  const toRoomPoint = (tileX: number, tileY: number) => {
    return {
      x: (tileX - tileY) * halfTileWidth,
      y: (tileX + tileY) * halfTileHeight,
    };
  };

  // Main tile lines (Pixi v8 path API: each segment must be explicitly stroked)
  for (let i = 0; i <= maxTileIndex; i++) {
    const a = toRoomPoint(i, 0);
    const b = toRoomPoint(i, maxTileIndex);
    target.poly([a.x, a.y, b.x, b.y], false);
    target.stroke({ width: GRID_LINE_WIDTH, color: GRID_LINE_COLOR, alpha: GRID_LINE_ALPHA });
  }

  for (let i = 0; i <= maxTileIndex; i++) {
    const a = toRoomPoint(0, i);
    const b = toRoomPoint(maxTileIndex, i);
    target.poly([a.x, a.y, b.x, b.y], false);
    target.stroke({ width: GRID_LINE_WIDTH, color: GRID_LINE_COLOR, alpha: GRID_LINE_ALPHA });
  }

  // Floor border (slightly thicker for readability)
  target.poly(
    [
      0, 0,
      -FLOOR_WIDTH / 2, FLOOR_HEIGHT / 2,
      0, FLOOR_HEIGHT,
      FLOOR_WIDTH / 2, FLOOR_HEIGHT / 2,
      0, 0,
    ],
    false
  );
  target.stroke({ width: GRID_BORDER_WIDTH, color: GRID_BORDER_COLOR, alpha: GRID_BORDER_ALPHA });
}

type AdminTileMode = 'footprint' | 'surface' | null;

/**
 * Drag footprint overlay: colour the item's footprint tiles green (valid) or
 * red (conflict). Extracted 1:1 from RoomCanvas.tsx onPointerMove (drawFpDiamond
 * + tile loops); conflict re-derivation stays on the caller side.
 */
export function drawDragFootprintOverlay(
  fpOvr: PIXI.Graphics,
  hasConflict: boolean,
  footprintTiles: Array<Array<number>> | null | undefined,
  isMirrored: boolean,
  centerTX: number,
  centerTY: number,
  depth: DepthComponents,
): void {
  fpOvr.clear();

  const ovrColor = hasConflict ? 0xFF3333 : 0x00CC44;
  const htw = TILE_WIDTH / 2;
  const hth = TILE_HEIGHT / 2;
  const drawFpDiamond = (tx: number, ty: number) => {
    const cx = (tx - ty) * htw;
    const cy = (tx + ty + 1) * hth;
    fpOvr.poly([cx, cy - hth, cx + htw, cy, cx, cy + hth, cx - htw, cy, cx, cy - hth], false);
    fpOvr.fill({ color: ovrColor, alpha: 0.35 });
    fpOvr.stroke({ width: 1, color: ovrColor, alpha: 0.65 });
  };

  if (footprintTiles && footprintTiles.length > 0) {
    for (const [dx, dy] of footprintTiles) {
      const [tdx, tdy] = mirrorFpOffset(dx, dy, isMirrored);
      drawFpDiamond(centerTX + tdx, centerTY + tdy);
    }
  } else {
    const baseFp2 = getOccupiedBaseTile(depth, isMirrored);
    for (let tx2 = 0; tx2 < baseFp2.overlayWidthTiles; tx2++) {
      for (let ty2 = 0; ty2 < baseFp2.overlayHeightTiles; ty2++) {
        drawFpDiamond(baseFp2.baseTileX + tx2, baseFp2.baseTileY + ty2);
      }
    }
  }

  fpOvr.visible = true;
}

/** Draw the live drag-preview rectangle for the admin tile editor. Extracted 1:1 from RoomCanvas.tsx (drawDragPreview); ref-reads stay in the component wrapper. */
export function drawAdminDragPreview(
  preview: PIXI.Graphics | null,
  mode: AdminTileMode,
  isAdd: boolean,
  center: { tileX: number; tileY: number },
  selectedTiles: ReadonlySet<string>,
  startTile: { tileX: number; tileY: number },
  endTile: { tileX: number; tileY: number },
): void {
  if (!preview) return;
  preview.clear();
  preview.visible = true;

  const fillColor = mode === 'footprint' ? (isAdd ? 0xffa500 : 0xFF4444) : (isAdd ? 0x8a2be2 : 0xFF4444);
  const strokeColor = mode === 'footprint' ? (isAdd ? 0xff8c00 : 0xFF0000) : (isAdd ? 0x6a0dad : 0xFF0000);
  const halfTW = TILE_WIDTH / 2;
  const halfTH = TILE_HEIGHT / 2;

  const minTX = Math.min(startTile.tileX, endTile.tileX);
  const maxTX = Math.max(startTile.tileX, endTile.tileX);
  const minTY = Math.min(startTile.tileY, endTile.tileY);
  const maxTY = Math.max(startTile.tileY, endTile.tileY);

  for (let tx = minTX; tx <= maxTX; tx++) {
    for (let ty = minTY; ty <= maxTY; ty++) {
      const dx = tx - center.tileX;
      const dy = ty - center.tileY;
      // Skip tiles that mode=remove but aren't selected
      if (!isAdd && !selectedTiles.has(`${dx},${dy}`)) continue;
      const cx = (tx - ty) * halfTW;
      const cy = (tx + ty + 1) * halfTH;
      preview.poly([cx, cy - halfTH, cx + halfTW, cy, cx, cy + halfTH, cx - halfTW, cy, cx, cy - halfTH], false);
      preview.fill({ color: fillColor, alpha: 0.55 });
      preview.stroke({ width: 2, color: strokeColor, alpha: 0.9 });
    }
  }
}

/**
 * Imperatively draw the admin tile selection overlay with explicit parameters.
 * Called both from the useEffect (for incremental updates) and directly from
 * executeRadialAction (for immediate feedback when pre-existing tiles load).
 */
export function drawAdminTileOverlayImperative(
  adminTileOverlay: PIXI.Graphics | null,
  mode: AdminTileMode,
  tiles: Iterable<string>,
  center: { tileX: number; tileY: number } | null,
): void {
  const overlay = adminTileOverlay;
  if (!overlay) return;
  overlay.clear();

  const tileArray = Array.from(tiles);
  if (mode === null || tileArray.length === 0) {
    overlay.visible = false;
    return;
  }

  overlay.visible = true;
  overlay.eventMode = 'none';

  const halfTileWidth = TILE_WIDTH / 2;
  const halfTileHeight = TILE_HEIGHT / 2;
  const fillColor = mode === 'footprint' ? 0xffa500 : 0x8a2be2;
  const c = center ?? { tileX: 0, tileY: 0 };

  for (const key of tileArray) {
    const [dxStr, dyStr] = key.split(',');
    const dx = parseInt(dxStr, 10);
    const dy = parseInt(dyStr, 10);
    const tileX = c.tileX + dx;
    const tileY = c.tileY + dy;
    const centerX = (tileX - tileY) * halfTileWidth;
    const centerY = (tileX + tileY + 1) * halfTileHeight;

    overlay.poly(
      [
        centerX, centerY - halfTileHeight,
        centerX + halfTileWidth, centerY,
        centerX, centerY + halfTileHeight,
        centerX - halfTileWidth, centerY,
        centerX, centerY - halfTileHeight,
      ],
      false
    );
    overlay.fill({ color: fillColor, alpha: 0.5 });
    overlay.stroke({ width: 2, color: mode === 'footprint' ? 0xff8c00 : 0x6a0dad, alpha: 0.9 });
  }
}
